import React, { useState } from "react";

interface DebugProps {
  data: any;
  initialCollapsed?: boolean;
  title?: string;
}

/**
 * Debug component for displaying any data in a collapsible pre-formatted block
 * Usage: <Debug data={someData} />
 *
 * @param props.data Any data to be displayed (objects, arrays, primitives)
 * @param props.initialCollapsed Whether the debug panel should start collapsed (default: false)
 * @param props.title Optional title for the debug panel
 */
export const Debug: React.FC<DebugProps> = ({
  data,
  initialCollapsed = false,
  title = "Debug Info",
}) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div
      className="debug-container"
      style={{
        margin: "10px 0",
        border: "1px dashed rgba(0, 0, 0, 0.2)",
        borderRadius: "4px",
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        fontFamily: "monospace",
        fontSize: "12px",
        overflow: "hidden",
      }}
    >
      <div
        onClick={toggleCollapse}
        style={{
          padding: "8px 12px",
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          userSelect: "none",
        }}
      >
        <div style={{ fontWeight: "bold", fontSize: "14px" }}>{title}</div>
        <div style={{ fontSize: "10px", color: "rgba(0, 0, 0, 0.5)" }}>
          {isCollapsed ? "▼ Show" : "▲ Hide"}
        </div>
      </div>

      {!isCollapsed ? (
        <pre
          style={{
            margin: 0,
            padding: "12px",
            maxHeight: "400px",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      ) : null}
    </div>
  );
};

export default Debug;
